import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import {
    EventsManager,
    FlowEvents,
    GameEvents,
    ReelsEvents,
    WinEvents,
} from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { ScatterStates } from "../../../abstracts/types/enums";
import { IBetWayWinData } from "../../../abstracts/types/interfaces";
import { getTotalFromArrayByPropertyName } from "../../../abstracts/utils/JSUtils";
import {
    fadeInBackgroundMusic,
    playNextBackgroundMusic,
} from "../../../abstracts/utils/SoundUtils";
import { DDD } from "../../DDD";
import DDDReel from "../../modules/Reels/DDDReel";
import { DDDReelsView } from "../../modules/Reels/DDDReelsView";
import { DDDScatterSymbol } from "../../modules/Reels/symbols/DDDScatterSymbol";
import { DDDSymbol } from "../../modules/Reels/symbols/DDDSymbol";
import { DDDWinView } from "../../modules/Winnings/DDDWinView";
import { DDDHighSymbolNames, DDDSpecialSymbolNames } from "../../types/DDDEnums";

export class DDDNormalIdlePresentation extends AbstractFlowPresentation {
    private DDDReels: DDDReel[];
    private winView: DDDWinView;
    private reelsView: DDDReelsView;
    private idleCall: gsap.core.Tween;
    private restartIdleCall: gsap.core.Tween;

    constructor() {
        super(`DDDNormalIdlePresentation`);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(FlowEvents.BET_STARTED, () => {
            this.killTimeline();

            this.winView.removeChild(this.winView.waysWinScreen);
            this.idleCall?.kill();
            this.restartIdleCall?.kill();
        });

        EventsManager.on(ReelsEvents.GET_REELS, (reels: DDDReel[]) => {
            this.DDDReels = reels;
        });

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (reelsView: DDDReelsView) => {
            this.reelsView = reelsView;
        });

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (winView: DDDWinView) => {
            this.winView = winView;
        });
    }

    protected override onDefaultStart() {
        super.onDefaultStart();
    }

    public start(flowStateController: FlowStateController) {
        super.start(flowStateController);

        this.finishPresentation();

        const isJurisdictionAllowed: boolean =
            DDD.apiPlugin.gameClientConfiguration.boostedBetAllowed;

        if (this.model.data === this.model.initData) {
            EventsManager.trigger(GameEvents.ENABLE_IDLE_ANIMATIONS, isJurisdictionAllowed);
        } else {
            this.idleCall = gsap.delayedCall(3 + Math.random() * 3, () => {
                EventsManager.trigger(GameEvents.ENABLE_IDLE_ANIMATIONS, isJurisdictionAllowed);
            });
        }

        if (!this.model.data.isRestore) {
            if (
                this.model.data.accumulatedRoundWin > 0 &&
                !DDD.model.isWinBelowStake() &&
                (this.model.data.mode === AbstractGameMode.NORMAL ||
                    (this.model.data.mode === AbstractGameMode.RESPIN &&
                        this.model.data.nextMode === AbstractGameMode.NORMAL))
            ) {
                this.generateWinningPresentation();
            }

            if (this.model.data.nextMode === AbstractGameMode.NORMAL) {
                if (this.model.data.mode === AbstractGameMode.RESPIN) {
                    fadeInBackgroundMusic(0.2);
                } else {
                    playNextBackgroundMusic();
                }
            }
        }
    }

    private generateWinningPresentation() {
        const isWinBelowStake: boolean =
            DDD.apiPlugin.gameClientConfiguration.belowStakeWinRestriction &&
            this.model.data.accumulatedRoundWin <= this.model.data.playedBetValue;

        if (isWinBelowStake) {
            this.flowPresentationTimeline.resume();
            return;
        }

        this.flowPresentationTimeline = gsap.timeline({
            repeat: -1,
            delay: 3,
            repeatDelay: 4,
        });

        const winningsArray = DDD.model.winningPos;

        if (DDD.model.gameData.betWayWins.length > 1 && !DDD.model.hasXGod) {
            // let hasAlreadyPlayedSound = false;

            DDD.model.gameData.betWayWins.forEach((betWayData: IBetWayWinData) => {
                let hasAlreadyPlayedSound = false;

                const wayTimeline = gsap.timeline({
                    smoothChildTiming: true,
                    autoRemoveChildren: false,
                    onStart: () => {
                        if (!hasAlreadyPlayedSound) {
                            if (betWayData.symbol === DDDHighSymbolNames.M1) {
                                // playSound({ name: DDDSoundNames.WIN_SWEEP_M1 });
                            } else if (betWayData.symbol === DDDHighSymbolNames.M2) {
                                // playSound({ name: DDDSoundNames.WIN_SWEEP_M2 });
                            } else if (betWayData.symbol === DDDHighSymbolNames.M3) {
                                // playSound({ name: DDDSoundNames.WIN_SWEEP_M3 });
                            } else if (betWayData.symbol === DDDHighSymbolNames.M4) {
                                // playSound({ name: DDDSoundNames.WIN_SWEEP_M4 });
                            } else if (betWayData.symbol === DDDHighSymbolNames.M5) {
                                // playSound({ name: DDDSoundNames.WIN_SWEEP_M5 });
                            }

                            hasAlreadyPlayedSound = true;
                        }
                    },
                });

                this.DDDReels.forEach((reel, reelIndex) => {
                    const visibleSymbols: AbstractSymbol[] = reel.getAllVisibleStopSymbols();
                    const reelWinSymbols = reel.getWinningSymbols(winningsArray[reelIndex]);
                    const wayWinSymbols: AbstractSymbol[] = [];

                    reelWinSymbols.forEach((symbol) => {
                        let newSymbol: DDDSymbol = symbol as DDDSymbol;

                        const cond1 = reelIndex < betWayData.length;
                        const cond2 = symbol.name === betWayData.symbol;

                        const cond3 =
                            symbol.name === DDDSpecialSymbolNames.XN_1 ||
                            symbol.name === DDDSpecialSymbolNames.XN_2 ||
                            symbol.name === DDDSpecialSymbolNames.XN_3;

                        const cond4 = symbol.name === DDDSpecialSymbolNames.WILD;

                        const cond5 =
                            symbol.name === DDDSpecialSymbolNames.SCATTER && symbol.isWild;

                        const cond6 = DDD.model.xNudgeMap[reelIndex] && !symbol.name.includes(`XN`);

                        if (
                            winningsArray[reelIndex][symbol.positionInArray - 1] &&
                            cond1 &&
                            (cond2 || cond3 || cond4 || cond5 || cond6)
                        ) {
                            let symbolWinAnimation: gsap.core.Tween | gsap.core.Timeline;

                            if (cond5) {
                                symbolWinAnimation = (symbol as DDDScatterSymbol).getWinAnimation(
                                    ScatterStates.WIN
                                );
                            } else if (cond6) {
                                newSymbol = reel.xNudgeSymbol!;
                                symbolWinAnimation = newSymbol.getWinAnimation();
                            } else {
                                symbolWinAnimation = symbol.getWinAnimation();
                            }
                            // const state: ScatterStates = cond5 ? ScatterStates.WIN : ``;

                            wayTimeline.add(symbolWinAnimation, 0);
                            wayWinSymbols.push(symbol);
                        } else if (winningsArray[reelIndex][0] && cond1 && cond3) {
                            let symbolWinAnimation = symbol.getWinAnimation();

                            wayTimeline.add(symbolWinAnimation, 0);
                            wayWinSymbols.push(symbol);
                        }
                    });

                    visibleSymbols.forEach((symbol) => {
                        if (!wayWinSymbols.includes(symbol as DDDSymbol)) {
                            wayTimeline.add((symbol as DDDSymbol).getNoWinAnimation(), 0);
                        }
                    });
                });

                wayTimeline.add(
                    this.winView.waysWinScreen.handleWaysWin(
                        [],
                        [betWayData.amount * betWayData.multiplier],
                        betWayData.numberOfCombinations
                    ),
                    0
                );

                this.flowPresentationTimeline.add(wayTimeline);
            });
        }

        //all ways handler
        if (DDD.model.gameData.betWayWins.length > 0) {
            const allWaysTimeline = gsap.timeline({});
            const symbolsIdleTimeline = gsap.timeline();

            this.DDDReels.forEach((reel, reelIndex) => {
                const winSymbols: AbstractSymbol[] = reel.getWinningSymbols(
                    winningsArray[reelIndex]
                );

                const alreadyUsedSymbols: AbstractSymbol[] = [];

                winSymbols.forEach((symbol) => {
                    let newSymbol: DDDSymbol = symbol as DDDSymbol;

                    if (symbol instanceof DDDScatterSymbol && symbol.isWild) {
                        allWaysTimeline.add(symbol.getWinAnimation(ScatterStates.WIN), 0);
                    } else if (symbol.name.includes(`B`) && this.model.data.brokeTheBank) {
                        allWaysTimeline.add(
                            (symbol as DDDSymbol).getMainSpine().getSpineTween(`win`),
                            0
                        );
                    } else {
                        if (DDD.model.xNudgeMap[reelIndex] && !symbol.name.includes(`XN`)) {
                            const xNudgeSymbol = reel.xNudgeSymbol!;

                            if (!alreadyUsedSymbols.includes(xNudgeSymbol)) {
                                newSymbol = xNudgeSymbol;
                                allWaysTimeline.add(newSymbol.getWinAnimation(), 0);
                            }
                        } else {
                            allWaysTimeline.add(symbol.getWinAnimation(), 0);
                        }
                    }

                    alreadyUsedSymbols.push(newSymbol);

                    symbolsIdleTimeline.add((symbol as DDDSymbol).getIdleAnimation(), 0);
                });

                const nonWinSymbols = reel.getNonWinningSymbols(winningsArray[reelIndex]);

                nonWinSymbols.forEach((symbol) => {
                    if (!alreadyUsedSymbols.includes(symbol as DDDSymbol) && symbol.visible) {
                        allWaysTimeline.add((symbol as DDDSymbol).getNoWinAnimation(), 0);
                    }
                });
            });

            let totalWaysCount: number = getTotalFromArrayByPropertyName(
                DDD.model.gameData.betWayWins,
                `numberOfCombinations`
            );

            if (DDD.model.hasXGod) {
                totalWaysCount = -1;
            }

            allWaysTimeline.add(
                this.winView.waysWinScreen.handleWaysWin(
                    [],
                    [this.model.data.accumulatedRoundWin],
                    totalWaysCount
                ),
                0
            );

            allWaysTimeline.add(symbolsIdleTimeline);

            this.flowPresentationTimeline.add(allWaysTimeline);
        } else if (DDD.model.gameData.betWayWins.length === 0 && DDD.model.gameData.brokeTheBank) {
            const allWaysTimeline = gsap.timeline({});
            const symbolsIdleTimeline = gsap.timeline();

            this.DDDReels.forEach((reel, reelIndex) => {
                const winSymbols: AbstractSymbol[] = reel.getWinningSymbols(
                    winningsArray[reelIndex]
                );

                const alreadyUsedSymbols: AbstractSymbol[] = [];

                winSymbols.forEach((symbol) => {
                    let newSymbol: DDDSymbol = symbol as DDDSymbol;

                    if (symbol.name.includes(`B`) && this.model.data.brokeTheBank) {
                        allWaysTimeline.add(
                            (symbol as DDDSymbol).getMainSpine().getSpineTween(`win`),
                            0
                        );
                    }

                    alreadyUsedSymbols.push(newSymbol);

                    symbolsIdleTimeline.add((symbol as DDDSymbol).getIdleAnimation(), 0);
                });

                const nonWinSymbols = reel.getNonWinningSymbols(winningsArray[reelIndex]);

                nonWinSymbols.forEach((symbol) => {
                    if (!alreadyUsedSymbols.includes(symbol as DDDSymbol) && symbol.visible) {
                        allWaysTimeline.add((symbol as DDDSymbol).getNoWinAnimation(), 0);
                    }
                });
            });

            allWaysTimeline.add(
                this.winView.waysWinScreen.handleWaysWin(
                    [],
                    [this.model.data.accumulatedRoundWin],
                    -1
                ),
                0
            );

            allWaysTimeline.add(symbolsIdleTimeline);

            this.flowPresentationTimeline.add(allWaysTimeline);
        }
    }
}
